import React, { useState } from "react";
import { Formik } from "formik";
import Button from "../../../../../components/common-components/button/Button";
import { BUTTON_VARIANTS } from "../../../../../components/common-components/button/Constants";
import { ABHA_HEALTH_RECORD_VIEW_NAVS, FORM_FIELDS_FOR_CONSENT_INIT, PURPOSE_OF_REQUEST_OPTIONS } from "../Constants";
import ConsentInitRequestPopup from "./consentInitRequestPopup";
import { FaFileMedical, FaNotesMedical, FaPills, FaHeartbeat, FaUserMd, FaSyringe, FaReceipt } from "react-icons/fa";
import * as Yup from 'yup';
import SelectDropdown from "../../../../../components/common-components/selectDropdown";
import DatePicker from "../../../../../components/common-components/datePicker";
import CustomDatePicker from "../../../../../components/common-components/CustomDatePicker";

const endOfToday = new Date();
endOfToday.setHours(23, 59, 59, 999);

const validationSchema = Yup.object().shape({
    [FORM_FIELDS_FOR_CONSENT_INIT.FROM]: Yup.date()
        .max(endOfToday, "From date cannot be in the future")
        .required("From date is required"),
    
    [FORM_FIELDS_FOR_CONSENT_INIT.TO]: Yup.date()
        .min(Yup.ref(FORM_FIELDS_FOR_CONSENT_INIT.FROM), "To date cannot be earlier than 'From' date")
        .max(endOfToday, "To date cannot be in the future")
        .required("To date is required"),

    [FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE]: Yup.date()
        .required('Expiry date is required')
        .test(
            'is at least 5 minutes ahead',
            'The selected time must be at least 5 minutes ahead of the current time.',
            function(value) {
                if (!value) return false;
                const now = new Date();
                const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
                return new Date(value) > fiveMinutesFromNow;
            }
        ),

    selectedHiTypes: Yup.array()
        .min(1, "At least one health information type must be selected"),

        [FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE]: Yup.object().shape({
            label: Yup.string().when([], {
                is: () => true,  // Ensuring it always runs the check
                then: (schema) => schema.required(`Purpose is required`),
                otherwise: (schema) => schema.notRequired(),
            }),
            value: Yup.string().when([], {
                is: () => true,
                then: (schema) => schema.required(`Purpose value is required`),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
});

const HI_TYPES = {
    DIAGNOSTIC: { label: "Diagnostic Report", icon: <FaFileMedical />, value: "DiagnosticReport" },
    DISCHARGE: { label: "Discharge Summary", icon: <FaNotesMedical />, value: "DischargeSummary" },
    PRESCRIPTION: { label: "Prescription", icon: <FaPills />, value: "Prescription" },
    WELLNESS: { label: "Wellness Record", icon: <FaHeartbeat />, value: "WellnessRecord" },
    OP_CONSULTATION: { label: "OP Consultation", icon: <FaUserMd />, value: "OPConsultation" },
    IMMUNIZATION: { label: "Immunization Record", icon: <FaSyringe />, value: "ImmunizationRecord" },
    INVOICE: { label: "Invoice", icon: <FaReceipt />, value: "Invoice" },
    HEALTH_DOCUMENT: { label: "Health Document Record", icon: <FaReceipt />, value: "HealthDocumentRecord" }
};

const ConsentInitRequest = ({ onConfirm, handleCancle }) => {
    const [isLoadingOpen, setIsLoadingOpen] = useState(false);

    return (
        <Formik
            initialValues={{
                [FORM_FIELDS_FOR_CONSENT_INIT.FROM]: '',
                [FORM_FIELDS_FOR_CONSENT_INIT.TO]: '',
                selectedHiTypes: [],
                [FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE]: '',
                [FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE]: {label: "", value: ""},
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                setIsLoadingOpen(true);
            }}
        >
            {({ values, setFieldValue, handleSubmit, handleBlur, errors, touched }) => (
                <div className="h-full flex flex-col gap-4 p-2">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800">Consent Initialization Request</h2>
                        <p className="text-gray-600">Select date range and health information types.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="h-full flex flex-col justify-between gap-4">
                        <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-[16px]">
                                <CustomDatePicker
                                    label="From"
                                    placeholder="Choose Date"
                                    isRequired={true}
                                    maxDate={new Date()}
                                    value={values[FORM_FIELDS_FOR_CONSENT_INIT.FROM]}
                                    name={FORM_FIELDS_FOR_CONSENT_INIT.FROM}
                                    onChangeCb={(date) => {
                                        setFieldValue(FORM_FIELDS_FOR_CONSENT_INIT.FROM, date);
                                    }}
                                    showTimeSelect={true}
                                    timeIntervals={5}
                                    filterTime={(time) => {
                                        const now = new Date();
                                        const selectedDate = new Date(time);
                                        
                                        if (selectedDate > now) return false;
                                        
                                        return true;
                                    }}
                                />
                                <CustomDatePicker
                                    label="To"
                                    placeholder="Choose Date"
                                    isRequired={true}
                                    maxDate={new Date()}
                                    minDate={values[FORM_FIELDS_FOR_CONSENT_INIT.FROM] ? new Date(values[FORM_FIELDS_FOR_CONSENT_INIT.FROM]) : undefined}
                                    value={values[FORM_FIELDS_FOR_CONSENT_INIT.TO]}
                                    name={FORM_FIELDS_FOR_CONSENT_INIT.TO}
                                    onChangeCb={(date) => {
                                        setFieldValue(FORM_FIELDS_FOR_CONSENT_INIT.TO, date);
                                    }}
                                    disabled={!values[FORM_FIELDS_FOR_CONSENT_INIT.FROM]}
                                    showTimeSelect={true}
                                    timeIntervals={1}
                                    filterTime={(time) => {
                                        const now = new Date();
                                        const selectedDate = new Date(time);
                                        const fromDate = values[FORM_FIELDS_FOR_CONSENT_INIT.FROM] ? new Date(values[FORM_FIELDS_FOR_CONSENT_INIT.FROM]) : null;
                                        
                                        if (selectedDate > now) return false;
                                        
                                        if (fromDate && selectedDate.toDateString() === fromDate.toDateString()) {
                                            return selectedDate >= fromDate;
                                        }
                                        
                                        return true;
                                    }}
                                />
                                <SelectDropdown
                                    placeholder="Select Purpose"
                                    isRequired
                                    label="Purpose of request"
                                    name={FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE}
                                    value={values[FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE]}
                                    onChangeCb={(val) => {
                                        setFieldValue(FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE, val);
                                    }}
                                    options={PURPOSE_OF_REQUEST_OPTIONS}
                                    onBlurCb={handleBlur}
                                />
                                <CustomDatePicker
                                    label="Expiry Date"
                                    placeholder="Choose Date"
                                    isRequired={true}
                                    minDate={new Date(Date.now() + 5 * 60 * 1000)}
                                    value={values[FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE]}
                                    name={FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE}
                                    onChangeCb={(date) => {
                                        if (date) {
                                            setFieldValue(FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE, date);
                                        } else {
                                            setFieldValue(FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE, '');
                                        }
                                    }}
                                    showTimeSelect={true}
                                    timeIntervals={5}
                                    filterTime={(time) => {
                                        const now = new Date();
                                        const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
                                        const selectedDate = new Date(time);
                                        
                                        if (selectedDate.toDateString() === now.toDateString()) {
                                            return selectedDate > fiveMinutesFromNow;
                                        }
                                        
                                        return selectedDate > now;
                                    }}
                                />
                            </div>

                            <div className="py-2 px-4 rounded-lg shadow bg-light">
                                <h3 className="text-lg font-semibold mb-2">
                                    Select Health Information Types <span className="text-red-600">*</span>
                                    {touched.selectedHiTypes && errors.selectedHiTypes && (
                                    <span className="text-red-500 text-[14px] font-[500]"> ({errors.selectedHiTypes})</span>
                                )}
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {Object.entries(HI_TYPES).map(([key, { label, icon, value }]) => (
                                        <div
                                            key={value}
                                            className={`p-2 border rounded-lg flex flex-col items-center cursor-pointer transition-all ${
                                                values.selectedHiTypes.includes(value)
                                                    ? "bg-met-secondary shadow-lg scale-95"
                                                    : "bg-white text-gray-800 hover:bg-gray-100"
                                            }`}
                                            onClick={() => {
                                                const updatedSelection = values.selectedHiTypes.includes(value)
                                                    ? values.selectedHiTypes.filter(t => t !== value)
                                                    : [...values.selectedHiTypes, value];
                                                setFieldValue("selectedHiTypes", updatedSelection);
                                            }}
                                        >
                                            <div className="text-2xl">{icon}</div>
                                            <span className="mt-2 text-sm font-medium">{label}</span>
                                        </div>
                                    ))}
                                </div>

                                <Button
                                    onClickCb={() => {
                                        const allSelected = values.selectedHiTypes.length === Object.keys(HI_TYPES).length;
                                        setFieldValue("selectedHiTypes", allSelected ? [] : Object.values(HI_TYPES).map(item => item.value));
                                    }}
                                    variant={BUTTON_VARIANTS.OUTLINED}
                                    customBtnClass="mt-3 w-100"
                                >
                                    {values.selectedHiTypes.length === Object.keys(HI_TYPES).length ? "Deselect All" : "Select All"}
                                </Button>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4">
                            <Button onClickCb={handleCancle} variant={BUTTON_VARIANTS.OUTLINED}>Cancel</Button>
                            <Button type="submit" variant={BUTTON_VARIANTS.CONTAINED} customBtnClass="px-6 py-2">
                                Submit
                            </Button>
                        </div>
                    </form>

                    {isLoadingOpen && (
                        <ConsentInitRequestPopup
                            onConfirm={() => onConfirm(ABHA_HEALTH_RECORD_VIEW_NAVS.CONSENT_STATUS)}
                            close={() => setIsLoadingOpen(false)}
                            data={{
                                from: values[FORM_FIELDS_FOR_CONSENT_INIT.FROM],
                                to: values[FORM_FIELDS_FOR_CONSENT_INIT.TO],
                                hiTypes: values.selectedHiTypes,
                                carePurpose: values[FORM_FIELDS_FOR_CONSENT_INIT.PURPOSE].value,
                                expiryDate: values[FORM_FIELDS_FOR_CONSENT_INIT.EXPIRY_DATE],
                            }}
                        />
                    )}
                </div>
            )}
        </Formik>
    );
};

export default ConsentInitRequest;
